import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme
} from '@mui/material';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ListSearchField } from '../../../List/list-search-field.component';
import { IListSearchPayload } from '../../../List/list.types';
import { ListPricePoliciesUseCase } from '../../../../Usecase/PricePolicies/list-price-policies-usecase';
import { useToast } from '../../../../Hooks/ToasContext';
import { IPricePoliciesEntity } from '../../../../Services/api/price-policies/price-policies.interface';
import { fetchAllPricePolicies } from '../../../../utils/fetch-all-price-policies';

interface ISelectPricePoliciesVariantProps {
    data?: any;
    onClose: () => void;
    onSelect: (values: IPricePoliciesEntity[]) => void;
    listPricePoliciesUseCase: ListPricePoliciesUseCase;
    selectedPolicies?: IPricePoliciesEntity[];
    multiple?: boolean;
}

const SelectPricePoliciesVariant: FC<ISelectPricePoliciesVariantProps> = ({
    onClose,
    onSelect,
    listPricePoliciesUseCase,
    selectedPolicies = [],
    multiple = true
}) => {
    const [loading, setLoading] = useState(false);
    const [allPolicies, setAllPolicies] = useState<IPricePoliciesEntity[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [internalSelectedPolicies, setInternalSelectedPolicies] = useState<IPricePoliciesEntity[]>(selectedPolicies);
    const { showToast } = useToast();
    const theme = useTheme();

    const loadPolicies = useCallback(async (searchBy?: string) => {
        try {
            if (searchBy?.trim()) {
                setIsSearching(true);
            } else {
                setLoading(true);
            }

            const policies = await fetchAllPricePolicies(listPricePoliciesUseCase, searchBy?.trim() || undefined);
            setAllPolicies(policies);
        } catch (error) {
            console.log('error loadPolicies:', error);
            showToast(
                error instanceof Error ? error.message : 'Erro ao carregar políticas de preço',
                'error'
            );
            setAllPolicies([]);
        } finally {
            setLoading(false);
            setIsSearching(false);
        }
    }, [listPricePoliciesUseCase, showToast]);

    const visiblePolicies = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) {
            return allPolicies;
        }

        return allPolicies.filter((policy) => {
            const name = policy.name?.toLowerCase() ?? '';
            const description = policy.description?.toLowerCase() ?? '';
            return name.includes(term) || description.includes(term);
        });
    }, [allPolicies, searchTerm]);

    const handleSearch = useCallback(({ searchBy }: IListSearchPayload) => {
        const nextSearch = searchBy ?? '';
        setSearchTerm(nextSearch);
        loadPolicies(nextSearch);
    }, [loadPolicies]);

    const handlePolicyToggle = (policy: IPricePoliciesEntity) => {
        if (multiple) {
            const isSelected = internalSelectedPolicies.some((p) => p.id === policy.id);

            if (isSelected) {
                setInternalSelectedPolicies((prev) => prev.filter((p) => p.id !== policy.id));
            } else {
                setInternalSelectedPolicies((prev) => [...prev, policy]);
            }
        } else {
            setInternalSelectedPolicies([policy]);
        }
    };

    const handleSelectAllVisible = () => {
        const allVisibleSelected = visiblePolicies.every((policy) =>
            internalSelectedPolicies.some((selected) => selected.id === policy.id)
        );

        if (allVisibleSelected) {
            const visibleIds = new Set(visiblePolicies.map((policy) => policy.id));
            setInternalSelectedPolicies((prev) => prev.filter((policy) => !visibleIds.has(policy.id)));
            return;
        }

        const merged = [...internalSelectedPolicies];
        visiblePolicies.forEach((policy) => {
            if (!merged.some((item) => item.id === policy.id)) {
                merged.push(policy);
            }
        });
        setInternalSelectedPolicies(merged);
    };

    const handleConfirm = () => {
        onSelect(internalSelectedPolicies);
        onClose();
    };

    const isPolicySelected = (policyId: string) =>
        internalSelectedPolicies.some((p) => p.id === policyId);

    const allVisibleSelected =
        visiblePolicies.length > 0 &&
        visiblePolicies.every((policy) => isPolicySelected(policy.id));

    const someVisibleSelected =
        visiblePolicies.some((policy) => isPolicySelected(policy.id)) && !allVisibleSelected;

    useEffect(() => {
        loadPolicies();
    }, [loadPolicies]);

    useEffect(() => {
        setInternalSelectedPolicies(selectedPolicies);
    }, [selectedPolicies]);

    return (
        <Dialog
            open
            keepMounted
            onClose={onClose}
            aria-describedby="select-price-policies-dialog"
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                    <Typography variant="h6">
                        {multiple ? 'Selecionar Políticas de Preço' : 'Selecionar Política de Preço'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {internalSelectedPolicies.length} selecionada(s)
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
                <Box mb={2}>
                    <ListSearchField
                        sx={{ width: '100%' }}
                        placeholder="Buscar por nome ou descrição..."
                        defaultValue={searchTerm}
                        onSearch={handleSearch}
                        minChars={0}
                        debounceMs={250}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                        {loading || isSearching
                            ? 'Carregando políticas...'
                            : searchTerm.trim()
                              ? `${visiblePolicies.length} política(s) encontrada(s)`
                              : `${allPolicies.length} política(s) disponível(is)`}
                    </Typography>
                </Box>

                {multiple && visiblePolicies.length > 0 && (
                    <Box mb={1.5}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={allVisibleSelected}
                                    indeterminate={someVisibleSelected}
                                    onChange={handleSelectAllVisible}
                                />
                            }
                            label={`Selecionar todas visíveis (${visiblePolicies.length})`}
                        />
                    </Box>
                )}

                <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                        maxHeight: 420,
                        overflow: 'auto',
                    }}
                >
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {multiple && <TableCell padding="checkbox">Seleção</TableCell>}
                                <TableCell>Nome</TableCell>
                                <TableCell>Descrição</TableCell>
                                <TableCell width={100}>Status</TableCell>
                                <TableCell width={90} align="center">Regras</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading || isSearching ? (
                                <TableRow>
                                    <TableCell colSpan={multiple ? 5 : 4} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={28} />
                                    </TableCell>
                                </TableRow>
                            ) : visiblePolicies.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={multiple ? 5 : 4} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">
                                            {searchTerm
                                                ? 'Nenhuma política encontrada para essa busca'
                                                : 'Nenhuma política de preço cadastrada'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                visiblePolicies.map((policy) => (
                                    <TableRow
                                        key={policy.id}
                                        hover
                                        onClick={() => handlePolicyToggle(policy)}
                                        sx={{
                                            cursor: 'pointer',
                                            backgroundColor: isPolicySelected(policy.id)
                                                ? theme.palette.action.selected
                                                : 'inherit',
                                        }}
                                    >
                                        {multiple && (
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={isPolicySelected(policy.id)}
                                                    onChange={() => handlePolicyToggle(policy)}
                                                    onClick={(event) => event.stopPropagation()}
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                                {policy.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {policy.description || 'Sem descrição'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                component="span"
                                                sx={{
                                                    px: 1,
                                                    py: 0.5,
                                                    borderRadius: 1,
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    backgroundColor: policy.isActive
                                                        ? theme.palette.success.light
                                                        : theme.palette.grey[300],
                                                    color: policy.isActive
                                                        ? theme.palette.success.contrastText
                                                        : theme.palette.text.secondary,
                                                }}
                                            >
                                                {policy.isActive ? 'Ativa' : 'Inativa'}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {policy.rules?.length || 0}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} color="inherit">
                    Cancelar
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={internalSelectedPolicies.length === 0}
                >
                    {multiple
                        ? `Confirmar (${internalSelectedPolicies.length})`
                        : 'Selecionar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SelectPricePoliciesVariant;
